import { graphql, useFragment } from "react-relay";
import { BaseWidgetProps } from "../BaseProps";
import {
  AHSeparator,
  AHText,
  AHWidgetTitleLogo,
  AHWidgetTitleText,
  AHWidgetTitleWrapper,
  Box,
  TouchableBox,
  useScaleFactor,
} from "arkh";
import {
  WidgetList_widgetList$data,
  WidgetList_widgetList$key,
} from "./__generated__/WidgetList_widgetList.graphql";
import { Platform, SectionList, ViewToken } from "react-native";
import { useImperativeHandle, useMemo, useRef } from "react";
import { Image } from "expo-image";
import { WidgetList_listItemInfo$key } from "./__generated__/WidgetList_listItemInfo.graphql";
import { useSetWidgetFocus } from "../useSetFocus";
import { useSetScrollFocusMutation } from "../Focus/useSetFocusScroll";
import { useDebouncedCallback } from "use-debounce";
import { FormattedMessage } from "react-intl";

type Props = {
  widget: WidgetList_widgetList$key;
  ref?: React.RefObject<WidgetListRef | null>;
} & BaseWidgetProps;

export type WidgetListRef = {
  scrollTo: (itemId: string) => void;
};

export const WidgetList = (props: Props) => {
  const widget = useFragment(
    graphql`
      fragment WidgetList_widgetList on WidgetList {
        id
        title
        dataSource
        sections {
          title
          items {
            title
            rightText
            rightTextSuffix
            info {
              ...WidgetList_listItemInfo
            }
          }
        }
      }
    `,
    props.widget
  );

  const listRef = useRef<
    SectionList<
      WidgetList_widgetList$data["sections"][number]["items"][number],
      {
        idx: number;
        title: string | null;
      }
    >
  >(null);
  useImperativeHandle(
    props.ref,
    () => ({
      scrollTo(itemId: string) {
        let m = itemId.match(/(\d+)_(\d+)$/);
        if (m) {
          listRef.current?.scrollToLocation({
            sectionIndex: parseInt(m[1]),
            itemIndex: parseInt(m[2]),
          });
        }
      },
    }),
    []
  );

  const sections = useMemo(
    () =>
      widget.sections.map((section, i) => ({
        idx: i,
        title: section.title,
        data: section.items,
      })) satisfies {
        idx: number;
        title: string | null;
        data: WidgetList_widgetList$data["sections"][number]["items"];
      }[],
    [widget.sections]
  );

  const isBokaklipp = widget.dataSource === "BOKAKLIPP";

  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const setScroll = useSetScrollFocusMutation(props.storecastId, widget.id);

  const onScroll = useDebouncedCallback(
    (e: { viewableItems: ViewToken<any>[] }) => {
      if (e.viewableItems[0]) {
        const id = `${e.viewableItems[0]?.section.idx}_${e.viewableItems[0]?.index ? e.viewableItems[0].index + 1 : 0}`;
        setScroll(id);
      }
    },
    200,
    {
      leading: true,
      maxWait: 1000,
    }
  );

  const sf = useScaleFactor();

  return (
    <TouchableBox
      disabled={props.mode !== "FOCUS" || props.isFocused}
      style={{ flex: 1 }}
      color="surface"
      onPress={props.interactive === false ? undefined : () => setFocus()}
    >
      <Box
        color="surface"
        cornerRadius="medium"
        style={{ width: "100%", height: "100%" }}
      >
        {isBokaklipp ? (
          <AHWidgetTitleLogo source={require("./BokaklippLogo.webp")} />
        ) : (
          <AHWidgetTitleWrapper
            style={Platform.OS !== "web" ? { flex: 0 } : undefined}
          >
            <AHWidgetTitleText>{widget.title}</AHWidgetTitleText>
          </AHWidgetTitleWrapper>
        )}

        <SectionList
          ref={listRef}
          sections={sections}
          contentContainerStyle={{ flex: 1 }}
          renderSectionHeader={({ section }) =>
            section.title ? (
              <Box>
                <AHText type="list-item-title-secondary" space="small">
                  {section.title}
                </AHText>
                <AHSeparator />
              </Box>
            ) : null
          }
          renderItem={({ item }) => (
            <Box
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottomWidth: 1,
              }}
              space="small"
              borderColor="border-primary"
            >
              <Box gap="small" style={{ flex: 1 }}>
                <AHText type="title-small" numberOfLines={1}>
                  {item.title}
                </AHText>
                {item.info && (
                  <Box
                    gap="small"
                    style={{ flexDirection: "row", flexWrap: "wrap" }}
                  >
                    {item.info?.map((info, i) => (
                      <ListItemInfo info={info} key={i} />
                    ))}
                  </Box>
                )}
              </Box>
              <Box
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexShrink: 0,
                }}
                gap="small"
                spaceLeft="small"
              >
                {item.rightText && (
                  <AHText type="title-small" numberOfLines={1}>
                    {item.rightText}
                  </AHText>
                )}
                {item.rightTextSuffix && (
                  <AHText type="list-item-title-secondary" numberOfLines={1}>
                    {item.rightTextSuffix}
                  </AHText>
                )}
              </Box>
            </Box>
          )}
          ListEmptyComponent={
            <Box
              style={{
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
              space="large"
            >
              <Box style={{ width: 220 * sf, height: 220 * sf }}>
                <Image
                  style={{ flex: 1 }}
                  contentFit="contain"
                  contentPosition="center"
                  source={require("./empty.svg")}
                />
              </Box>
              <AHText type="subtitle" spaceBottom="xlarge">
                <FormattedMessage
                  defaultMessage="There is nothing to show here right now"
                  description="WidgetList empty list title"
                />
              </AHText>
            </Box>
          }
          onViewableItemsChanged={props.isFocused ? onScroll : undefined}
        />
      </Box>
    </TouchableBox>
  );
};

const ListItemInfo = (props: { info: WidgetList_listItemInfo$key }) => {
  const info = useFragment(
    graphql`
      fragment WidgetList_listItemInfo on WidgetListItemInfo {
        __typename
        ... on StoreZonedDate {
          storeFormattedDate(style: DEFAULT)
        }
        ... on WidgetListItemInfoText {
          text
          italic
        }
      }
    `,
    props.info
  );

  switch (info.__typename) {
    case "StoreZonedDate":
      return <AHText numberOfLines={1}>{info.storeFormattedDate}</AHText>;
    case "WidgetListItemInfoText":
      return (
        <AHText
          numberOfLines={1}
          style={{ fontStyle: info.italic ? "italic" : undefined }}
        >
          {info.text}
        </AHText>
      );
    default:
      return null;
  }
};
